import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Building2, Key, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * BankDataForm - Formulário para dados bancários do criador
 * Usado no Dashboard do criador para configurar dados de pagamento
 */
const BankDataForm = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Obter token do localStorage
  const getToken = () => localStorage.getItem('token');
  
  // Estado dos dados bancários
  const [bankData, setBankData] = useState({
    pix_key: '',
    pix_key_type: 'cpf',
    bank_code: '',
    bank_name: '',
    agency: '',
    agency_digit: '',
    account_number: '',
    account_digit: '',
    account_type: 'corrente',
    holder_name: '',
    holder_document: ''
  });
  
  // Estado atual salvo
  const [savedData, setSavedData] = useState(null);

  // Lista de bancos brasileiros
  const brazilianBanks = [
    { code: '001', name: 'Banco do Brasil' },
    { code: '033', name: 'Santander' },
    { code: '104', name: 'Caixa Econômica' },
    { code: '237', name: 'Bradesco' },
    { code: '341', name: 'Itaú' },
    { code: '260', name: 'Nubank' },
    { code: '077', name: 'Inter' },
    { code: '336', name: 'C6 Bank' },
    { code: '212', name: 'Original' },
    { code: '655', name: 'Neon' },
    { code: '290', name: 'PagBank' },
    { code: '380', name: 'PicPay' },
    { code: '323', name: 'Mercado Pago' }
  ];

  useEffect(() => {
    loadBankData();
  }, []);

  const loadBankData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) return;
      
      const response = await axios.get(`${API}/api/users/me/bank-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedData(response.data);
    } catch (error) {
      console.error('Erro ao carregar dados bancários:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBankData(prev => ({ ...prev, [name]: value }));
    
    // Auto-preencher nome do banco quando selecionar código
    if (name === 'bank_code') {
      const bank = brazilianBanks.find(b => b.code === value);
      if (bank) {
        setBankData(prev => ({ ...prev, bank_name: bank.name }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações
    if (!bankData.pix_key) {
      toast.error('Informe sua chave PIX');
      return;
    }
    
    try {
      setSaving(true);
      const token = getToken();
      await axios.put(`${API}/api/users/me/bank-data`, bankData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Dados bancários salvos com sucesso!');
      setShowForm(false);
      loadBankData();
    } catch (error) {
      console.error('Erro ao salvar dados bancários:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar dados bancários');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border border-[#C79F5E]/20 rounded-2xl p-6 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
        <div className="h-4 bg-white/10 rounded w-2/3"></div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border border-[#C79F5E]/20 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#4D1A4A]/30 to-[#C79F5E]/20 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C79F5E] to-[#8B6914] flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Dados para Pagamento</h3>
              <p className="text-sm text-gray-400">Configure como receber seus ganhos</p>
            </div>
          </div>
          
          {savedData?.has_pix && !showForm && (
            <div className="flex items-center gap-2 bg-green-500/20 px-3 py-1.5 rounded-full">
              <Check className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-400">Configurado</span>
            </div>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      <div className="p-6">
        {!showForm && savedData ? (
          // Visualização dos dados salvos
          <div className="space-y-4">
            {savedData.has_pix ? (
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Key className="w-5 h-5 text-[#C79F5E]" />
                  <span className="text-gray-400">Chave PIX</span>
                </div>
                <p className="text-white font-mono text-lg">{savedData.pix_key}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Tipo: {savedData.pix_key_type === 'cpf' ? 'CPF' : 
                         savedData.pix_key_type === 'email' ? 'E-mail' :
                         savedData.pix_key_type === 'phone' ? 'Telefone' : 'Chave Aleatória'}
                </p>
              </div>
            ) : (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-200 font-semibold">Chave PIX não configurada</p>
                  <p className="text-sm text-yellow-200/70">Configure sua chave PIX para receber seus pagamentos.</p>
                </div>
              </div>
            )}

            {savedData.has_bank_account && (
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-5 h-5 text-[#C79F5E]" />
                  <span className="text-gray-400">Conta Bancária (Backup)</span>
                </div>
                <p className="text-white">{savedData.bank_data?.bank_name}</p>
                <p className="text-sm text-gray-400">
                  Ag: {savedData.bank_data?.agency} | Conta: {savedData.bank_data?.account_number}
                </p>
                <p className="text-xs text-gray-500 mt-1">{savedData.bank_data?.holder_name}</p>
              </div>
            )}

            <button
              onClick={() => setShowForm(true)}
              className="w-full py-3 bg-gradient-to-r from-[#C79F5E] to-[#D4AF37] text-black font-bold rounded-xl hover:opacity-90 transition-all"
            >
              {savedData.has_pix ? 'Alterar Dados' : 'Configurar Dados Bancários'}
            </button>
          </div>
        ) : (
          // Formulário de edição
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Seção PIX */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Key className="w-5 h-5 text-[#C79F5E]" />
                Chave PIX <span className="text-xs text-red-400">*Obrigatório</span>
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Tipo de Chave</label>
                  <select
                    name="pix_key_type"
                    value={bankData.pix_key_type}
                    onChange={handleInputChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#C79F5E] focus:outline-none"
                  >
                    <option value="cpf">CPF</option>
                    <option value="email">E-mail</option>
                    <option value="phone">Telefone</option>
                    <option value="random">Chave Aleatória</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Chave PIX</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="pix_key"
                      value={bankData.pix_key}
                      onChange={handleInputChange}
                      placeholder={
                        bankData.pix_key_type === 'cpf' ? '000.000.000-00' :
                        bankData.pix_key_type === 'email' ? 'seu@email.com' :
                        bankData.pix_key_type === 'phone' ? '+55 (00) 00000-0000' :
                        'Chave aleatória'
                      }
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#C79F5E] focus:outline-none pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Seção Conta Bancária */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#C79F5E]" />
                Conta Bancária <span className="text-xs text-gray-400">(Backup - Opcional)</span>
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Banco</label>
                  <select
                    name="bank_code"
                    value={bankData.bank_code}
                    onChange={handleInputChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#C79F5E] focus:outline-none"
                  >
                    <option value="">Selecione o banco</option>
                    {brazilianBanks.map(bank => (
                      <option key={bank.code} value={bank.code}>
                        {bank.code} - {bank.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Tipo de Conta</label>
                  <select
                    name="account_type"
                    value={bankData.account_type}
                    onChange={handleInputChange}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#C79F5E] focus:outline-none"
                  >
                    <option value="corrente">Conta Corrente</option>
                    <option value="poupanca">Poupança</option>
                  </select>
                </div>
                
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-sm text-gray-400 mb-1">Agência</label>
                    <input
                      type="text"
                      name="agency"
                      value={bankData.agency}
                      onChange={handleInputChange}
                      placeholder="0000"
                      maxLength="4"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#C79F5E] focus:outline-none"
                    />
                  </div>
                  <div className="w-16">
                    <label className="block text-sm text-gray-400 mb-1">Dígito</label>
                    <input
                      type="text"
                      name="agency_digit"
                      value={bankData.agency_digit}
                      onChange={handleInputChange}
                      placeholder="0"
                      maxLength="1"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#C79F5E] focus:outline-none text-center"
                    />
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-sm text-gray-400 mb-1">Conta</label>
                    <input
                      type="text"
                      name="account_number"
                      value={bankData.account_number}
                      onChange={handleInputChange}
                      placeholder="00000000"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#C79F5E] focus:outline-none"
                    />
                  </div>
                  <div className="w-16">
                    <label className="block text-sm text-gray-400 mb-1">Dígito</label>
                    <input
                      type="text"
                      name="account_digit"
                      value={bankData.account_digit}
                      onChange={handleInputChange}
                      placeholder="0"
                      maxLength="1"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#C79F5E] focus:outline-none text-center"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Nome do Titular</label>
                  <input
                    type="text"
                    name="holder_name"
                    value={bankData.holder_name}
                    onChange={handleInputChange}
                    placeholder="Nome completo"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#C79F5E] focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">CPF/CNPJ do Titular</label>
                  <input
                    type="text"
                    name="holder_document"
                    value={bankData.holder_document}
                    onChange={handleInputChange}
                    placeholder="000.000.000-00"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#C79F5E] focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-3 bg-gradient-to-r from-[#C79F5E] to-[#D4AF37] text-black font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar Dados'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BankDataForm;
